<template>
  <div class="flex flex-col flex-1 h-full p-2">
    <div class="text-wrap mb-2">
      <h2 class="text-base font-semibold font-mono">
        <a :href="`https://polymarket.com/event/${market.slug}`" target="_blank">
          {{ market.question }}
          <span v-if="market.closed" class="text-red-800">[CLOSED]</span>
        </a>
      </h2>
    </div>
    <div v-if="size(market.nevuaTimeseriesHistory) > 0" class="graph w-full h-32 relative justify-start mb-2 align-middle" ref="chartContainer"></div>
    <div v-else><img :src="`https://img.nevua.markets${market.nevuaSVGLink}`" class="h-full w-full"/></div>  
    <div class="text-xs mb-2 text-wrap font-mono text-center">
      <p>
        <span v-if="market.closed" class="text-red-800">CLOSED</span>
        <span v-else>{{ timeUntilEnd }}</span>
        | Volume: <span class="font-bold text-teal-600">{{ formattedVolume }}</span>
      </p>
    </div>
  </div>
</template>
<!-- <div class="context-column-w">
        {{ event.volume ? event.volume.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }) : 'N/A' }}
      </div>
      <div class="context-column-w">{{ event.commentCount }}</div> -->
<script>
/* eslint-disable */
/* global LightweightCharts */
import moment from 'moment';
import { createChart } from 'lightweight-charts';
import _ from 'lodash';

export default {
  name: 'MarketChart',
  
  props: {
    market: {
      type: Object,
      required: true
    },
    event: {
      type: Object,
      required: false
    }
  },

  data() {
    return {
      chartContainer: null,
      rollingWindowSize: 3,
      aroonPlot: false,
      useVariance: false
    }
  },

  computed: {
    timeUntilEnd() {
      const endDate = moment(this.market.endDate);
      return `Ends in ${endDate.fromNow(true)}`;
    },

    formattedVolume() {
      const numericVolume = parseFloat(this.market.volume);
      return numericVolume ? numericVolume.toLocaleString('en-US', { 
        style: 'currency', 
        currency: 'USD', 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      }) : 'N/A';
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.renderChart();
    });
  },

  methods: {
    size(obj) {
      return _.size(obj);
    },
    applyCustomTickMarks(chart, formattedData) {
      const firstTick = formattedData[0].time;
      const lastTick = formattedData[formattedData.length - 1].time;
      const secondFifthTick = formattedData[Math.floor((formattedData.length / 5) * 2)].time;
      const thirdFifthTick = formattedData[Math.floor((formattedData.length / 5) * 3)].time;
      const fourthFifthTick = formattedData[Math.floor((formattedData.length / 5) * 4)].time;

      let secondFifthSet = false;
      let fourthFifthSet = false;

      const tickInInterval = (time, start, end) => {
        return time >= start && time <= end;
      }

      chart.timeScale().applyOptions({
        tickMarkFormatter: (time, tickMarkType, locale) => {
          if (
            !tickInInterval(time, secondFifthTick, thirdFifthTick) &&
            !tickInInterval(time, fourthFifthTick, lastTick)
          ) {
            return ''
          }

          if (
            (tickInInterval(time, secondFifthTick, thirdFifthTick) && secondFifthSet) ||
            (tickInInterval(time, fourthFifthTick, lastTick) && fourthFifthSet)
          ) {
            return ''
          }
          
          if(tickInInterval(time, secondFifthTick, thirdFifthTick) && !secondFifthSet) {
            secondFifthSet = true;
          } else if(tickInInterval(time, fourthFifthTick, lastTick) && !fourthFifthSet) {
            fourthFifthSet = true;
          }

          const date = new Date(time * 1000);
          const month = date.toLocaleString(locale, { month: 'short' });
          const day = date.getDate();
          return `${month} ${day}`;
        }
      });
    },

    renderChart() {
      const container = this.$refs.chartContainer;
      if (!this.market.nevuaTimeseriesHistory || !this.market.nevuaTimeseriesHistory.length) {
        container.innerHTML = '<div class="text-xs text-gray-500 h-full flex items-center justify-center">No price history available</div>';
        return;
      }

      const chart = createChart(container, {
        width: container.clientWidth,
        height: container.clientHeight,
        layout: {
          textColor: 'rgba(0, 0, 0, 0.5)',
          background: { type: 'solid', color: 'white' },
          fontSize: 10,
          attributionLogo: false,
        },
        grid: {
          vertLines: { visible: false },
          horzLines: { visible: false }
        },
        crosshair: {
          mode: 1,
          vertLine: {
            visible: false,
            labelVisible: false
          },
          horzLine: {
            visible: false,
            labelVisible: false
          }
        },
        handleScroll: {
          mouseWheel: false,
          pressedMouseMove: false,
          horzTouchDrag: false,
          vertTouchDrag: false
        },
        handleScale: {
          axisPressedMouseMove: false,
          mouseWheel: false,
          pinch: false
        },
        timeScale: {
          timeVisible: false,
          secondsVisible: false,
          borderVisible: false
        },
        rightPriceScale: {
          borderVisible: false,
          ticksVisible: true
        }
      });

      const formattedData = this.market.nevuaTimeseriesHistory.map((datapoint) => {
        const ratio = datapoint.p * 100;
        const r = Math.round((ratio/100) * 255);
        const g = Math.round((1 - ratio/100) * 128);
        const b = Math.round((1 - ratio/100) * 255);
        
        return {
          time: datapoint.t,
          value: ratio,
          color: `rgba(${r}, ${g}, ${b}, 1)`
        };
      });

      const lineSeries = chart.addLineSeries({
        color: 'rgba(0, 0, 0, 0)',
        lineWidth: 2,
        lastValueVisible: false,
        priceLineVisible: false
      });

      lineSeries.setData(formattedData);
      chart.timeScale().fitContent();

      // Optional: Call the custom tick marks function
      //this.applyCustomTickMarks(chart, formattedData);

      // Floating label (tooltip) implementation
      const toolTipWidth = 80; // Aligns with w-20 (5rem or 80px)
      const toolTipHeight = 56; // Aligns with h-14 (3.5rem or 56px)
      const toolTipMargin = 15;

      // Create and style the tooltip html element
      const toolTip = document.createElement('div');
      toolTip.className = 'absolute hidden box-border text-xs text-left z-20 pointer-events-none border rounded-sm bg-white text-black border-teal-500';
      toolTip.classList.add('w-20', 'h-14');
      container.appendChild(toolTip);

      // update tooltip
      chart.subscribeCrosshairMove(param => {
          if (
              param.point === undefined ||
              !param.time ||
              param.point.x < 0 ||
              param.point.x > container.clientWidth ||
              param.point.y < 0 ||
              param.point.y > container.clientHeight
          ) {
              toolTip.classList.add('hidden');
          } else {
              // time will be in the same format that we supplied to setData.
              // thus it will be YYYY-MM-DD
              const date = new Date(param.time * 1000); // Convert UNIX timestamp to JavaScript Date object
              const month = date.toLocaleString('default', { month: 'short' });
              const day = date.getDate();
              const year = date.getFullYear();
              const dateStr = `${month} ${day} ${year}`;
              toolTip.classList.remove('hidden');
              const data = param.seriesData.get(lineSeries); // Use lineSeries here
              const price = data.value !== undefined ? data.value : data.close;
              toolTip.innerHTML = `<div class="text-xl text-black">
                  ${Math.round(price)}%
                  </div><div class="text-black">
                  ${dateStr}
                  </div>`;

              const y = param.point.y;
              let left = param.point.x + toolTipMargin;
              if (left > container.clientWidth - toolTipWidth) {
                  left = param.point.x - toolTipMargin - toolTipWidth;
              }

              let top = y + toolTipMargin;
              if (top > container.clientHeight - toolTipHeight) {
                  top = y - toolTipHeight - toolTipMargin;
              }
              toolTip.style.left = left + 'px';
              toolTip.style.top = top + 'px';
          }
      });
    }
  }
}
</script>

<style scoped>
/* .event-chart {
  @apply  p-4 border rounded-lg shadow-md w-full md:w-1/3 lg:w-1/4 m-2;
} */

.graph, .context-column-w {
  @apply mb-2; /* Add margin-bottom to separate the rows */
}

@media (max-width: 768px) {
  .event-chart {
    width: calc(50% - 1rem); /* Adjust for 2 cards per row on smaller screens */
  }
}

@media (max-width: 480px) {
  .event-chart {
    width: calc(100% - 1rem); /* Full width for single column on very small screens */
  }
}
</style>